import { FieldPath, FieldValues, RegisterOptions, useFormContext } from "react-hook-form";

import { FilterableSinglePickerFormField } from "src/components/Form/FilterableSinglePickerFormField";
import { normalizeString } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.mappings";
import { useGetPropertiesQuery } from "src/graphql/anti-fraud/queries/rules/PropertiesQuery";

export type PropertyFilterableSinglePickerFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  id: string;
  label?: string;
  helperText?: string;
  placeholder?: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  name: TName;
  caseTypeCode?: string;
};

export function PropertyFilterableSinglePickerFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  name,
  rules,
  label,
  helperText,
  placeholder,
  caseTypeCode,
}: PropertyFilterableSinglePickerFormFieldProps<TFieldValues, TName>) {
  const { setError, control } = useFormContext<TFieldValues, TName>();

  const { data: items, loading } = useGetPropertiesQuery({
    onError: (error) => setError(name, error),
    caseTypeCode,
  });

  return (
    <FilterableSinglePickerFormField
      id={id}
      control={control}
      filterOption={({ option, search, getOptionLabel }) =>
        normalizeString(getOptionLabel(option)).includes(normalizeString(search))
      }
      name={name}
      rules={rules}
      label={label}
      helperText={helperText}
      options={items}
      getOptionLabel={(item) => `${item.prefix} - ${item.propertyName}`}
      getOptionValue={(item) => {
        if (item.elementCode) {
          return `${item.elementCode}-${item.propertyCode}`;
        }
        return item.propertyCode;
      }}
      loading={loading}
      placeholder={placeholder}
    />
  );
}
